import { useCallback } from "react";
import { useLogout } from "./useLogout";
import { useOverlay } from "./OverlayProvider";
import { useSession } from "./SessionProvider";

/**
 * **Ask to log out — one control, one predicate, every trigger.**
 *
 * S2 §11, LOCKED: a **registered** player logs out immediately and a **guest**
 * gets the dialog first, because logging out of a guest account destroys it and
 * everything in it.
 *
 * This was `AppShell.requestLogout`, serving the drawer's row and the flyout's
 * row. Ivy's profile §7.1 adds a third trigger — the Account card's sign-out
 * row — and is explicit about how it must arrive:
 *
 * > *"**The same control as the drawer's Log out**, including its predicate —
 * > extract it, do not fork it."* (§15 rule 9 repeats it as a rule Rafi may not
 * > improvise around: *do not fork the guest log-out predicate*.)
 *
 * So the predicate moved here **unchanged** rather than being restated on a
 * third surface. A second copy of "is this a guest" is how one surface ends up
 * destroying an account without asking.
 */
export function useRequestLogout(): () => void {
  const { profile } = useSession();
  const { open } = useOverlay();
  const logout = useLogout();

  return useCallback(() => {
    if (profile?.isGuest) {
      open("guest-logout");
      return;
    }
    void logout();
  }, [logout, open, profile]);
}
